import { type ReactElement, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { cn } from '@/lib/utils'

const WINDOW_SIZE = 5

interface ColumnMonthNavProps {
  label: string
  year: number
  monthIndex: number
  onChange: (year: number, monthIndex: number) => void
  className?: string
}

/** Converts absolute month number back to [year, monthIndex]. */
function fromAbsolute(abs: number): [number, number] {
  return [Math.floor(abs / 12), ((abs % 12) + 12) % 12]
}

export function ColumnMonthNav({
  label,
  year,
  monthIndex,
  onChange,
  className,
}: ColumnMonthNavProps): ReactElement {
  const { t } = useTranslation('common')
  const months = [
    t('monthsShort.jan'), t('monthsShort.feb'), t('monthsShort.mar'),
    t('monthsShort.apr'), t('monthsShort.may'), t('monthsShort.jun'),
    t('monthsShort.jul'), t('monthsShort.aug'), t('monthsShort.sep'),
    t('monthsShort.oct'), t('monthsShort.nov'), t('monthsShort.dec'),
  ]

  const now = new Date()
  const currentAbsolute = now.getFullYear() * 12 + now.getMonth()
  const selected = year * 12 + monthIndex
  const [windowStart, setWindowStart] = useState(() => selected - 2)
  const [prevSelected, setPrevSelected] = useState(selected)

  // Keep selected month visible: shift window when selection goes out of bounds
  // Uses React's recommended "adjust state during render" pattern (no useEffect)
  if (prevSelected !== selected) {
    setPrevSelected(selected)
    if (selected < windowStart) {
      setWindowStart(selected)
    } else if (selected > windowStart + WINDOW_SIZE - 1) {
      setWindowStart(selected - WINDOW_SIZE + 1)
    }
  }

  const window = Array.from({ length: WINDOW_SIZE }, (_, i) => {
    const abs = windowStart + i
    const [y, m] = fromAbsolute(abs)
    return { year: y, month: m, label: months[m], active: abs === selected, future: abs > currentAbsolute }
  })

  const isAtCurrentMonth = selected >= currentAbsolute

  const prev = (): void => {
    const [y, m] = fromAbsolute(selected - 1)
    onChange(y, m)
  }
  const next = (): void => {
    if (isAtCurrentMonth) return
    const [y, m] = fromAbsolute(selected + 1)
    onChange(y, m)
  }

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <h2 className="shrink-0 text-2xl font-bold text-foreground">{label}</h2>
      <ButtonGroup className="flex-1 items-center">
        <Button
          variant="outline"
          size="sm"
          className="h-11 w-11 px-0 shrink-0"
          onClick={prev}
          aria-label="Previous month"
        >
          <ChevronLeft className="size-5 text-muted-foreground" />
        </Button>

        {window.map(({ year: y, month: m, label: mLabel, active, future }) => (
          <Button
            key={`${y}-${m}`}
            variant="outline"
            size="sm"
            disabled={future}
            className={cn(
              'h-11 flex-1 rounded-none border px-3 text-sm transition-all',
              active
                ? 'bg-muted-selector text-foreground shadow-sm ring-1 ring-inset ring-white/15 z-10'
                : 'border-border text-muted-foreground',
              future && 'opacity-40 pointer-events-none',
            )}
            onClick={() => onChange(y, m)}
          >
            <span className="inline-flex items-baseline gap-1.5">
              <span>{mLabel}</span>
              {active && <span className="text-xs opacity-70">{y}</span>}
            </span>
          </Button>
        ))}

        <Button
          variant="outline"
          size="sm"
          className="h-11 w-11 px-0 shrink-0"
          onClick={next}
          disabled={isAtCurrentMonth}
          aria-label="Next month"
        >
          <ChevronRight className="size-5 text-muted-foreground" />
        </Button>
      </ButtonGroup>
    </div>
  )
}
